import Store from "../store/store";
import SleepLogHttpService, { SummaryListQuery } from "./SleepLogHttpService";
import { HttpException } from "../../types/Exceptions";
import { SleepLogSummary, SleepLogSummaryListItem, SummaryPeriod } from "../../types/SleepLogSummary";
import SummaryPeriodUtils from "../../utils/summary-period-utils";
import { formatDateAsLocalTime } from "../../utils/FormatUtility";

const MAX_PERIODS_PER_ENSURE = 12;
const MAX_IDS_PER_REQUEST = 24;
const POLL_INITIAL_DELAY_MS = 2000;
const POLL_MAX_DELAY_MS = 10000;
const POLL_TIMEOUT_MS = 6 * 60 * 1000;

export function todayLocal() {
    return formatDateAsLocalTime(new Date());
}

export function isSummaryPending(summary: Pick<SleepLogSummaryListItem, "status">) {
    return summary.status === "queued" || summary.status === "generating";
}

function isFeatureDisabled(e: unknown) {
    return e instanceof HttpException && e.statusCode === 404;
}

export interface SleepLogSummariesState {
    /** Every summary loaded for the current user, keyed by id. */
    summaries: ReadonlyMap<string, SleepLogSummaryListItem>;
    /** False once the server reports that summaries aren't available. */
    available: boolean;
}

const initialState = (): SleepLogSummariesState => ({ summaries: new Map(), available: true });

/**
 * App-wide cache of period summaries for the logged-in user. Every view (diary list, Explore tab, detail page)
 * reads from and writes to the same cache, so an update made in one place shows up everywhere.
 *
 * The service also polls summaries that are being generated, backing off from 2s to 10s. Each summary gives up
 * after POLL_TIMEOUT_MS (longer than the server's 5-minute generation lease, so recovered jobs are still picked up).
 * The backoff and timeout restart when a summary becomes pending again (e.g. after Generate or Recalculate).
 */
export default class SleepLogSummariesService extends Store<SleepLogSummariesState> {
    // Periods already passed to "ensure" for this user, so scrolling back and forth doesn't repeat requests.
    private requestedPeriods = new Set<string>();
    // When each pending summary started being polled.
    private pollStarts = new Map<string, number>();
    private pollAttempt = 0;
    private pollTimer: ReturnType<typeof setTimeout> | undefined;
    private isPolling = false;
    // Incremented when the user changes so responses for the previous user are dropped.
    private generation = 0;

    constructor(private username: string, private sleepLogHttpService: SleepLogHttpService) {
        super(initialState());
    }

    setUser(username: string) {
        if (username === this.username) {
            return;
        }

        this.username = username;
        this.generation++;
        this.stopPolling();
        this.pollStarts.clear();
        this.requestedPeriods.clear();
        this.state = initialState();
        this.notifySubscribers();
    }

    /**
     * Loads exactly the requested periods and creates their missing summaries.
     * Aborting `signal` stops creating further summaries.
     */
    async load(periods: SummaryPeriod[], signal?: AbortSignal) {
        if (!this.username || !this.state.available || periods.length === 0 || signal?.aborted) {
            return;
        }

        const generation = this.generation;

        try {
            const { summaries } = await this.sleepLogHttpService.getSummaries({ ids: periods.map(SummaryPeriodUtils.toKey) });

            if (generation !== this.generation) {
                return;
            }

            const loaded = new Set(summaries.map(s => s.id));
            this.upsert(summaries);
            await this.ensure(periods.filter(p => !loaded.has(SummaryPeriodUtils.toKey(p))), signal);
        }
        catch (e) {
            if (generation !== this.generation) {
                return;
            }

            if (isFeatureDisabled(e)) {
                this.markUnavailable();
            }
            else {
                console.log("Loading period summaries failed.", e);
            }
        }
    }

    /**
     * Creates the summaries of the latest completed week and month, so new users see summaries without opening the
     * diary first. Throws if summaries aren't available; other errors are ignored and retried on the next call.
     */
    async ensureRecentPeriods() {
        const periods = SummaryPeriodUtils.latestCompletedPeriods(todayLocal());

        try {
            await this.ensure(periods);
        }
        catch (e) {
            if (isFeatureDisabled(e)) {
                this.markUnavailable();
                throw e;
            }
        }
    }

    /** Fetches summaries from the server and adds them to the cache. */
    async getSummaries(query: SummaryListQuery) {
        const generation = this.generation;

        try {
            const result = await this.sleepLogHttpService.getSummaries(query);

            if (generation === this.generation) {
                this.upsert(result.summaries);
            }

            return result;
        }
        catch (e) {
            if (generation === this.generation && isFeatureDisabled(e)) {
                this.markUnavailable();
            }

            throw e;
        }
    }

    async generate(id: string) {
        return this.runAction(id, async () => {
            if (!this.state.summaries.has(id)) {
                const period = SummaryPeriodUtils.fromKey(id);

                if (period) {
                    const { summaries } = await this.sleepLogHttpService.ensureSummaries(
                        todayLocal(),
                         [{ type: period.type, startDate: period.startDate }]
                    );
                    this.requestedPeriods.add(id);
                    this.upsert(summaries);

                    const ensured = summaries.find(s => s.id === id);

                    if (ensured && (isSummaryPending(ensured) || ensured.status === "ready")) {
                        return ensured;
                    }
                }
            }

            return this.sleepLogHttpService.generateSummary(id);
        });
    }

    async recalculate(id: string) {
        return this.runAction(id, () => this.sleepLogHttpService.recalculateSummary(id));
    }

    /** Adds or replaces summaries in the cache and starts polling the ones that are being generated. */
    upsert(items: SleepLogSummaryListItem | SleepLogSummaryListItem[]) {
        this.merge(Array.isArray(items) ? items : [items], false);
    }

    private async runAction(id: string, action: () => Promise<SleepLogSummary>) {
        const generation = this.generation;
        const updated = await action();

        if (generation === this.generation) {
            this.merge([updated], true);
        }

        return updated;
    }

    private merge(items: SleepLogSummaryListItem[], restartPolling: boolean) {
        if (items.length === 0) {
            return;
        }

        const now = Date.now();
        const summaries = new Map(this.state.summaries);
        let restart = false;

        for (const item of items) {
            const listItem = item;
            const prev = summaries.get(listItem.id);
            summaries.set(listItem.id, listItem);

            if (!isSummaryPending(listItem)) {
                this.pollStarts.delete(listItem.id);
            }
            else if (restartPolling || !this.pollStarts.has(listItem.id) || (prev && !isSummaryPending(prev))) {
                this.pollStarts.set(listItem.id, now);
                restart = true;
            }
        }

        this.state = { ...this.state, summaries };
        this.notifySubscribers();

        if (restart) {
            this.pollAttempt = 0;
            this.clearPollTimer();
        }

        this.schedulePoll();
    }

    private markUnavailable() {
        if (this.state.available) {
            this.state = { ...this.state, available: false };
            this.notifySubscribers();
        }
    }

    private pollableIds() {
        const now = Date.now();

        return [...this.pollStarts]
            .filter(([, start]) => now - start <= POLL_TIMEOUT_MS)
            .map(([id]) => id)
            .sort()
            .slice(0, MAX_IDS_PER_REQUEST);
    }

    private schedulePoll() {
        if (this.pollTimer !== undefined || this.isPolling || !this.username || this.pollableIds().length === 0) {
            return;
        }

        const delay = Math.min(POLL_MAX_DELAY_MS, POLL_INITIAL_DELAY_MS * Math.pow(1.5, this.pollAttempt));
        this.pollTimer = setTimeout(() => this.poll(), delay);
    }

    private async poll() {
        this.pollTimer = undefined;
        const ids = this.pollableIds();

        if (ids.length === 0) {
            return;
        }

        const generation = this.generation;
        this.isPolling = true;
        this.pollAttempt++;

        try {
            const { summaries } = await this.sleepLogHttpService.getSummaries({ ids });

            if (generation === this.generation) {
                this.upsert(summaries);
            }
        }
        catch {
            // Keep polling until the timeout; transient errors are expected on flaky networks.
        }
        finally {
            if (generation === this.generation) {
                this.isPolling = false;
                this.schedulePoll();
            }
        }
    }

    private clearPollTimer() {
        if (this.pollTimer !== undefined) {
            clearTimeout(this.pollTimer);
            this.pollTimer = undefined;
        }
    }

    private stopPolling() {
        this.clearPollTimer();
        this.isPolling = false;
        this.pollAttempt = 0;
    }

    private async ensure(periods: SummaryPeriod[], signal?: AbortSignal) {
        const generation = this.generation;
        const missing = periods.filter(p => !this.requestedPeriods.has(SummaryPeriodUtils.toKey(p)));

        for (let i = 0; i < missing.length && !signal?.aborted && generation === this.generation; i += MAX_PERIODS_PER_ENSURE) {
            const chunk = missing.slice(i, i + MAX_PERIODS_PER_ENSURE);
            const keys = chunk.map(SummaryPeriodUtils.toKey);
            keys.forEach(key => this.requestedPeriods.add(key));

            try {
                const { summaries } = await this.sleepLogHttpService.ensureSummaries(
                    todayLocal(),
                    chunk.map(period => ({ type: period.type, startDate: period.startDate }))
                );

                if (generation === this.generation) {
                    this.upsert(summaries);
                }
            }
            catch (e) {
                if (generation === this.generation) {
                    keys.forEach(key => this.requestedPeriods.delete(key));
                }

                throw e;
            }
        }
    }
}
